/* @flow */

import clsx from 'clsx';
import * as React from 'react';
import { connect } from 'react-redux';

import type { GamestreamToken } from '../../../helpers/gaming/types';
import type { Undefined } from '../../../helpers/jsHelpers/types';
import type { NETGEM_API_V8_METADATA } from '../../../libs/netgemLibrary/v8/types/MetadataProgram';
import type { NETGEM_API_V8_URL_DEFINITION } from '../../../libs/netgemLibrary/v8/types/NtgVideoFeed';
import type { CombinedReducers } from '../../../redux/reducers';
import type { Dispatch } from '../../../redux/types/types';
import type { CARD_DATA_MODAL_TYPE } from '../../modal/cardModal/CardModalConstsAndTypes';
import type { CONFIRMATION_DATA_MODAL_TYPE } from '../../modal/confirmationModal/ConfirmationModal';
import type { EULA_DATA_MODAL_TYPE } from '../../modal/eulaModal/EulaModal';
import type { GAME_PROFILE_SELECT_DATA_MODAL_TYPE } from '../../modal/gameProfileSelectModal/GameProfileSelectModal';
import type { MESSAGE_MODAL_TYPE } from '../../modal/messageModal/MessageModal';
import type { CloseCallbackType } from '../../modal/vodPurchaseModal/VodPurchaseModal';

import { logError, logWarning, showDebug } from '../../../helpers/debug/debug';
import GameManager from '../../../helpers/gaming/gameManager';
import { fireAndForget } from '../../../helpers/jsHelpers/promise';
import Localizer from '../../../helpers/localization';
import { Messenger, MessengerEvents } from '../../../helpers/messenger';
import PlayingHistoryCache from '../../../helpers/playingHistory/PlayingHistoryCache';
import { type BASE_VOD_PURCHASE_DATA_TYPE, PendingOperationKind, PendingOperationReason, type VOD_PURCHASE_DATA_TYPE, storePendingOperation } from '../../../helpers/rights/pendingOperations';
import { isPlaybackGranted } from '../../../helpers/rights/playback';
import { TILE_SYNOPSIS_LINE_HEIGHT, TILE_SYNOPSIS_TAGLINE_LINE_HEIGHT } from '../../../helpers/ui/constants';
import { getDistributorId } from '../../../helpers/ui/item/distributor';
import { isVodItem } from '../../../helpers/ui/item/metadata';
import { ExtendedItemType } from '../../../helpers/ui/item/types';
import { BroadcastStatus } from '../../../helpers/ui/location/Format';
import { getSynopsis, getTagline, renderProgramDetails } from '../../../helpers/ui/metadata/Format';
import { PROGRAM_INFO } from '../../../helpers/ui/metadata/Types';
import { getCardUrlDefinitionForItem } from '../../../helpers/ui/section/tile';
import { BOVodAssetStatus, getVodLocationFromVtiId } from '../../../helpers/videofutur/metadata';
import { isPackPurchased } from '../../../helpers/videofutur/pack';
import ContinuousWatching from '../../../helpers/viewingHistory/ContinuousWatching';
import { ItemContent, ItemLocationKind, ItemType, type NETGEM_API_V8_FEED_ITEM } from '../../../libs/netgemLibrary/v8/types/FeedItem';
import { type NETGEM_API_PLAYINGHISTORY_ITEM, playingHistoryArrayToObject } from '../../../libs/netgemLibrary/v8/types/PlayingHistory';
import { TileConfig, TileConfigTileType, TileOnFocus } from '../../../libs/netgemLibrary/v8/types/WidgetConfig';
import { FEATURE_SUBSCRIPTION } from '../../../redux/appConf/constants';
import { RegistrationType } from '../../../redux/appRegistration/types/types';
import { hideModal, showConfirmationModal, showEulaModal, showGameProfileSelectModal, showMessageModal, showVodPurchaseModal } from '../../../redux/modal/actions';
import { getSettingValueByName } from '../../../redux/netgemApi/actions/helpers/settings';
import { updatePlayingHistory } from '../../../redux/netgemApi/actions/personalData/playingHistory';
import getGamestreamToken from '../../../redux/netgemApi/actions/v8/getGamestreamToken';
import ButtonFX from '../../buttons/ButtonFX';
import { HeightKind, WidthKind } from '../../buttons/types';
import InfiniteCircleLoader from '../../loader/infiniteCircleLoader';
import PricingVod, { ContextKind as PricingContext } from '../../pricingVod/PricingVod';
import RecordButton, { DisplayMode, ContextKind as RecordContext } from '../../recordButton/RecordButton';
import ResizeDetectorWrapper, { type ResizeType } from '../../resizeDetectorWrapper/ResizeDetectorWrapper';
import TextScroller from '../../textScroller/TextScroller';
import { getGameAlias, getTimeText, getWatchingStatus, renderProgramTitle, renderSeriesTitle, searchMostRecentProfileForGame } from './helper';
import './ItemOverlay.css';
import {
  type CompleteItemOverlayPropType,
  type ItemOverlayPropType,
  type ItemOverlayStateType,
  type ReduxItemOverlayDispatchToPropsType,
  type ReduxItemOverlayReducerStateType,
  TruncateState,
} from './ItemOverlayConstsAndTypes';

const InitialState = Object.freeze({
  // Here "undefined" means "not initialized", "null" means "no continuous watching", and otherwise it's a regular ContinuousWatching instance
  continuousWatching: undefined,
  continuousWatchingButtonText: '',
  isLoading: true,
  maxLines: 0,
  synopsis: null,
  truncateState: TruncateState.Initializing,
});

class ItemOverlayView extends React.PureComponent<CompleteItemOverlayPropType, ItemOverlayStateType> {
  constructor(props: CompleteItemOverlayPropType) {
    super(props);
    this.state = { ...InitialState };
  }

  componentDidMount() {
    const { innerRef } = this.props;

    if (innerRef) {
      // Expose the class instance
      innerRef(this);
    }

    this.updateSynopsis();
  }

  componentDidUpdate(prevProps: CompleteItemOverlayPropType) {
    const {
      contentType,
      isVisible,
      item: { type },
      programMetadata,
      programTitle,
      seriesMetadata,
      tvLocationMetadata,
      vodLocationsMetadata,
    } = this.props;
    const {
      isVisible: prevIsVisible,
      programMetadata: prevProgramMetadata,
      programTitle: prevProgramTitle,
      seriesMetadata: prevSeriesMetadata,
      tvLocationMetadata: prevTVLocationMetadata,
      vodLocationsMetadata: prevVodLocationsMetadata,
    } = prevProps;
    const { continuousWatching, continuousWatchingButtonText } = this.state;

    if (isVisible && isVisible !== prevIsVisible) {
      fireAndForget(this.initializeContinuousWatching(), 'ItemOverlay.initializeContinuousWatching');
    }

    if (programMetadata !== prevProgramMetadata || seriesMetadata !== prevSeriesMetadata) {
      this.updateSynopsis();
    }

    if (
      (prevVodLocationsMetadata === null && vodLocationsMetadata !== null) ||
      (prevTVLocationMetadata === null && tvLocationMetadata !== null) ||
      ((type === ItemType.Universe || contentType === ItemContent.Game) && programTitle !== prevProgramTitle) ||
      (type === ItemType.Series && prevSeriesMetadata === null && seriesMetadata !== null) ||
      (continuousWatchingButtonText !== '' && continuousWatching !== undefined && continuousWatching !== null)
    ) {
      this.setIsReady();
    }
  }

  componentWillUnmount() {
    const { innerRef } = this.props;

    if (innerRef) {
      innerRef(null);
    }

    Messenger.off(MessengerEvents.CONTINUOUS_WATCHING_BUTTON_TEXT_UPDATED, this.updateContinuousWatchingButtonText);
  }

  setIsReady = () => {
    this.setState({ isLoading: false });
  };

  updateContinuousWatchingButtonText = (isReady: boolean, seriesId: string, index: number, continuousWatchingButtonText: string) => {
    this.setState({ continuousWatchingButtonText });
  };

  initializeContinuousWatching = async () => {
    const { item, seriesMetadata } = this.props;

    if (!seriesMetadata || !isVodItem(item)) {
      // Not a series
      this.setState({ continuousWatching: null });
      return;
    }

    const { id: seriesId, providerInfo } = seriesMetadata;

    Messenger.on(MessengerEvents.CONTINUOUS_WATCHING_BUTTON_TEXT_UPDATED, this.updateContinuousWatchingButtonText);
    const continuousWatching = await ContinuousWatching.create(item, providerInfo?.viewingHistoryId ?? seriesId);

    this.setState({ continuousWatching });
  };

  // eslint-disable-next-line react/no-unused-class-component-methods
  showDebugInfo = () => {
    const { state } = this;

    showDebug('Section Item Overlay', {
      misc: { watchingStatus: this.localGetWatchingStatus() },
      state,
      stateFields: ['continuousWatching', 'continuousWatchingButtonText', 'isLoading', 'synopsis', 'truncateState'],
    });
  };

  updateSynopsis = () => {
    const {
      contentType,
      hoverContent,
      item: { locType, type },
      programMetadata,
      seriesMetadata,
      tvLocationMetadata,
      vodLocationsMetadata,
    } = this.props;

    let assetMetadata: ?NETGEM_API_V8_METADATA = null;
    let synopsis: string | null = '';

    if (hoverContent.has(TileConfig.SeriesSynopsis)) {
      assetMetadata = seriesMetadata;
    } else if (hoverContent.has(TileConfig.ProgramSynopsis)) {
      assetMetadata = programMetadata;
    }

    if (assetMetadata) {
      if (locType === ItemLocationKind.TVOD || locType === ItemLocationKind.EST || locType === ItemLocationKind.Game) {
        // Display tag line on VOD and game tiles
        synopsis = getTagline(seriesMetadata ?? programMetadata, Localizer.language) ?? '';
      } else {
        // Display synopsis on all other tiles
        synopsis = assetMetadata ? getSynopsis(assetMetadata, Localizer.language) : '';
      }
    }

    this.setState({ synopsis, truncateState: TruncateState.Initializing });

    if (
      (contentType === ItemContent.NetgemSVOD && type !== ItemType.Series) ||
      (contentType === ItemContent.VODOrDeeplink && type === ItemType.Series) ||
      vodLocationsMetadata !== null ||
      tvLocationMetadata !== null
    ) {
      // Do not wait for location metadata to load for these contents or if metadata is already loaded
      this.setIsReady();
    }
  };

  localGetWatchingStatus = (): number | null => {
    const { contentType, item, programMetadata, seriesMetadata, viewingHistory, viewingHistoryStatus, vodStatus } = this.props;

    return getWatchingStatus(item, viewingHistory, viewingHistoryStatus, contentType, programMetadata, seriesMetadata, vodStatus);
  };

  getTimeElement = (description: Set<TileConfig>, className?: string): React.Node => {
    const { item, now } = this.props;

    const time = getTimeText(item, now, description);

    if (!time) {
      return null;
    }

    return <div className={clsx('text', className)}>{time}</div>;
  };

  getDetailsElement = (description: Set<TileConfig>): React.Node => {
    const {
      item,
      item: { locType },
      programMetadata,
      vodLocationsMetadata,
    } = this.props;

    let info = 0;

    if (description.has(TileConfig.Year)) {
      info |= PROGRAM_INFO.ReleaseDate;
    }

    if (description.has(TileConfig.Genre) || locType === ItemLocationKind.Game) {
      info |= PROGRAM_INFO.Genre;
    }

    if (description.has(TileConfig.Language)) {
      info |= PROGRAM_INFO.Language;
    }

    if (description.has(TileConfig.ParentalGuidance) || locType === ItemLocationKind.Game) {
      info |= PROGRAM_INFO.ParentalGuidance;
    }

    if (info === 0) {
      return null;
    }

    return <div className='details other'>{renderProgramDetails(item, programMetadata, vodLocationsMetadata, info)}</div>;
  };

  handlePlayGameButtonOnClick = (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement>): Promise<void> => {
    event.preventDefault();
    event.stopPropagation();

    const {
      appConfiguration,
      gameLocationMetadata,
      item,
      item: { selectedProgramId },
      localGetGamestreamToken,
      localShowConfirmation,
      localShowEula,
      localShowGameProfileSelect,
      localShowMessage,
      localUpdatePlayingHistory,
      playingHistory,
      playingHistoryStatus,
      programMetadata,
    } = this.props;

    if (gameLocationMetadata === null) {
      logError('Cannot launch game with gameLocationMetadata = null');
      return Promise.resolve();
    }

    const isGamepadOnly = programMetadata?.gameMetadata?.isMouseAndKeyboard === false;

    const gameAlias = getGameAlias(gameLocationMetadata);
    const profileId = searchMostRecentProfileForGame(playingHistoryStatus, playingHistory, selectedProgramId);
    const avatarBaseUrl = getSettingValueByName('account.service.gamestream', 'avatarsurl', appConfiguration) ?? '';

    if (gameAlias === null) {
      logError('Game alias missing in metadata');
      return Promise.resolve();
    }

    // If profileId is null, the first profile of the list will be used
    return GameManager.launchGame({
      avatarBaseUrl,
      gameAlias,
      getGamestreamToken: localGetGamestreamToken,
      isGamepadOnly,
      item,
      playingHistory: playingHistoryArrayToObject(playingHistory),
      playingHistoryStatus,
      profileId,
      promptSessionClose: localShowConfirmation,
      showEula: localShowEula,
      showGameProfileSelect: localShowGameProfileSelect,
      showMessage: localShowMessage,
      updatePlayingHistoryItem: localUpdatePlayingHistory,
    });
  };

  handlePlayButtonOnClick = (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const {
      authority,
      channels,
      commercialOffers,
      contentType,
      defaultOnItemClick,
      defaultRights,
      isRegisteredAsGuest,
      isSubscriptionFeatureEnabled,
      item,
      localHideModal,
      onItemClick,
      previewCatchupScheduledEventId,
      programMetadata,
      seriesMetadata,
      tileConfig: { type: tileType },
      tvLocationMetadata,
      userRights,
      viewingHistoryId,
      vodLocationsMetadata,
      vtiId,
    } = this.props;

    const locationMetadata = tvLocationMetadata ?? getVodLocationFromVtiId(vodLocationsMetadata, vtiId);
    const distributorId = locationMetadata?.location?.providerInfo?.distributorId ?? null;

    const cardData: CARD_DATA_MODAL_TYPE = {
      distributorId,
      item,
      programMetadata,
      seriesMetadata,
      tileType,
      urlDefinition: undefined,
      viewingHistoryId,
      vtiId,
    };

    if (tvLocationMetadata) {
      // In case of TV item, add the program/series card URL definition (if found) so that the episodes and the related items will be displayed on the card
      const urlDefinition = getCardUrlDefinitionForItem(item, seriesMetadata, onItemClick, defaultOnItemClick);
      if (urlDefinition !== null) {
        cardData.urlDefinition = urlDefinition;
      }
    }

    if (!isPlaybackGranted(isRegisteredAsGuest, isSubscriptionFeatureEnabled, cardData, authority, contentType, locationMetadata?.location, channels, commercialOffers, defaultRights, userRights)) {
      // Playback denied: log-in or subscribe page has been requested
      localHideModal();
      return;
    }

    if (contentType === ItemContent.VODOrDeeplink) {
      if (programMetadata && vtiId) {
        Messenger.emit(MessengerEvents.OPEN_PLAYER, {
          distributorId,
          item,
          locationId: locationMetadata?.location.id,
          programMetadata,
          seriesMetadata,
          tileType,
          type: ExtendedItemType.VOD,
          viewingHistoryId,
          vtiId,
        });
      }
    } else {
      Messenger.emit(MessengerEvents.OPEN_PLAYER, {
        authority,
        item,
        previewCatchupScheduledEventId,
        programMetadata,
        seriesMetadata,
        type: ExtendedItemType.TV,
      });
    }
  };

  handlePlaySeriesButtonOnClick = async (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const {
      authority,
      channels,
      commercialOffers,
      contentType,
      defaultOnItemClick,
      defaultRights,
      isRegisteredAsGuest,
      isSubscriptionFeatureEnabled,
      localHideModal,
      onItemClick,
      previewCatchupScheduledEventId,
      seriesMetadata,
      tileConfig: { type: tileType },
      tvLocationMetadata,
      userRights,
    } = this.props;
    const { continuousWatching } = this.state;

    if (continuousWatching === undefined || continuousWatching === null) {
      logWarning('Missing continuousWatching');
      return;
    }

    const resumeData = await continuousWatching.getData();

    if (resumeData === null) {
      logWarning('Cannot build resume data');
      return;
    }

    const { distributorId, item, locationMetadata, programMetadata, viewingHistoryId, vtiId } = resumeData;

    const cardData: CARD_DATA_MODAL_TYPE = {
      distributorId,
      item,
      programMetadata,
      seriesMetadata,
      tileType,
      urlDefinition: undefined,
      viewingHistoryId,
      vtiId,
    };

    if (tvLocationMetadata) {
      // In case of TV item, add the program/series card URL definition (if found) so that the episodes and the related items will be displayed on the card
      const urlDefinition = getCardUrlDefinitionForItem(item, seriesMetadata, onItemClick, defaultOnItemClick);
      if (urlDefinition !== null) {
        cardData.urlDefinition = urlDefinition;
      }
    }

    if (!isPlaybackGranted(isRegisteredAsGuest, isSubscriptionFeatureEnabled, cardData, authority, contentType, locationMetadata?.location, channels, commercialOffers, defaultRights, userRights)) {
      // Playback denied: log-in or subscribe page has been requested
      localHideModal();
      return;
    }

    if (contentType === ItemContent.VODOrDeeplink) {
      if (programMetadata && vtiId) {
        Messenger.emit(MessengerEvents.OPEN_PLAYER, {
          continuousWatching,
          distributorId,
          item,
          locationId: locationMetadata?.location.id,
          programMetadata,
          seriesMetadata,
          tileType,
          type: ExtendedItemType.VOD,
          viewingHistoryId,
          vtiId,
        });
      }
    } else {
      Messenger.emit(MessengerEvents.OPEN_PLAYER, {
        authority,
        continuousWatching,
        item,
        previewCatchupScheduledEventId,
        programMetadata,
        seriesMetadata,
        type: ExtendedItemType.TV,
      });
    }
  };

  vodPurchaseModalClosedCallback = (data?: CloseCallbackType): void => {
    const { item, programMetadata, purchaseInfo, seriesMetadata, vodLocationsMetadata } = this.props;

    if (!data) {
      // Purchase canceled
      return;
    }

    const { locationId, playNow, viewingHistoryId, vtiId } = data;

    if (playNow && vtiId !== undefined) {
      Messenger.emit(MessengerEvents.OPEN_PLAYER, {
        distributorId: getDistributorId(vodLocationsMetadata, purchaseInfo),
        item,
        locationId,
        programMetadata,
        seriesMetadata,
        type: ExtendedItemType.VOD,
        viewingHistoryId,
        vtiId,
      });
    }
  };

  handlePurchaseButtonOnClick = (data: BASE_VOD_PURCHASE_DATA_TYPE): void => {
    const {
      defaultOnItemClick,
      isRegisteredAsGuest,
      item,
      localHideModal,
      onItemClick,
      programMetadata,
      seriesMetadata,
      showVodPurchaseDialog,
      tileConfig: { type: tileType },
    } = this.props;

    const { definition, displayPrice, distributorId, itemCount, licenseDuration, locationId, otherVtiId, purchaseType, vodLocationMetadata, vtiId } = data;

    if (!programMetadata) {
      return;
    }

    const providerInfo = vodLocationMetadata?.providerInfo ?? programMetadata.providerInfo;

    const purchaseData: VOD_PURCHASE_DATA_TYPE = {
      definition,
      displayPrice,
      distributorId,
      item,
      itemCount,
      licenseDuration,
      locationId,
      otherVtiId,
      programMetadata,
      purchaseType,
      seriesMetadata,
      viewingHistoryId: providerInfo?.viewingHistoryId,
      vodLocationMetadata,
      vtiId,
    };

    if (isRegisteredAsGuest) {
      // Guest mode: let's open the sign-in page

      const cardData: CARD_DATA_MODAL_TYPE = {
        item,
        pendingOperation: {
          operationType: PendingOperationKind.Purchase,
          purchaseData,
          reason: PendingOperationReason.RequireAccount,
        },
        programMetadata,
        seriesMetadata,
        tileType,
      };

      // In case of TV item, add the program/series card URL definition (if found) so that the episodes and the related items will be displayed on the card
      const urlDefinition = getCardUrlDefinitionForItem(item, seriesMetadata, onItemClick, defaultOnItemClick);
      if (urlDefinition !== null) {
        cardData.urlDefinition = urlDefinition;
      }

      storePendingOperation(cardData);
      localHideModal();
      Messenger.emit(MessengerEvents.SHOW_SIGN_IN, PendingOperationKind.Purchase);
    } else {
      Messenger.once(MessengerEvents.MODAL_CLOSED, this.vodPurchaseModalClosedCallback);
      showVodPurchaseDialog(purchaseData);
    }
  };

  getWatchButtonLabel = (progress: number | null): string => (progress !== null ? 'common.actions.resume' : 'common.actions.watch');

  renderSinglePlayButton = (): React.Node => {
    const {
      tileConfig: { type: tileType },
      vodStatus,
    } = this.props;

    const status = vodStatus?.status ?? BOVodAssetStatus.Unknown;

    if (![BOVodAssetStatus.Free, BOVodAssetStatus.Rented, BOVodAssetStatus.Bought].includes(status)) {
      return null;
    }

    const widthKind = tileType === TileConfigTileType.LandscapeBig || tileType === TileConfigTileType.LandscapeVod ? WidthKind.Large : WidthKind.Stretched;
    const progress = this.localGetWatchingStatus();

    return (
      <ButtonFX allowZeroProgress={false} heightKind={HeightKind.Medium} key='play' onClick={this.handlePlayButtonOnClick} progress={progress} widthKind={widthKind}>
        {Localizer.localize(this.getWatchButtonLabel(progress))}
      </ButtonFX>
    );
  };

  renderSeriesPlayButton = (): React.Node => {
    const {
      isRegisteredAsGuest,
      item: { locType },
      purchaseInfo,
      purchaseList,
      tileConfig: { type: tileType },
    } = this.props;
    const { continuousWatching, continuousWatchingButtonText } = this.state;

    if (isRegisteredAsGuest || continuousWatching === undefined || continuousWatching === null) {
      return null;
    }

    const widthKind = tileType === TileConfigTileType.LandscapeBig || tileType === TileConfigTileType.LandscapeVod ? WidthKind.Large : WidthKind.Stretched;

    if (locType === undefined || locType === null) {
      return null;
    }

    if (![ItemLocationKind.Catchup, ItemLocationKind.Recording, ItemLocationKind.SVOD].includes(locType) && (purchaseInfo === null || !isPackPurchased(purchaseInfo, purchaseList))) {
      // Series not purchased or wrong type of content
      return null;
    }

    // Season item has been purchased: display "Watch"/"Resume" button
    return (
      <ButtonFX heightKind={HeightKind.Medium} isLoading={continuousWatchingButtonText === ''} key='play' onClick={this.handlePlaySeriesButtonOnClick} widthKind={widthKind}>
        {continuousWatchingButtonText}
      </ButtonFX>
    );
  };

  renderVodMainButton = (): React.Node => {
    const { seriesMetadata } = this.props;

    return (
      <>
        {this.renderPricingVod()}
        {seriesMetadata !== null ? this.renderSeriesPlayButton() : this.renderSinglePlayButton()}
      </>
    );
  };

  renderGameMainButton = (): React.Node => {
    const {
      gameLocationMetadata,
      tileConfig: { type: tileType },
    } = this.props;

    if (gameLocationMetadata === null) {
      // Metadata not loaded yet
      return null;
    }

    const widthKind = tileType === TileConfigTileType.LandscapeBig || tileType === TileConfigTileType.LandscapeVod ? WidthKind.Large : WidthKind.Stretched;

    return (
      <ButtonFX heightKind={HeightKind.Medium} key='play' onClick={this.handlePlayGameButtonOnClick} widthKind={widthKind}>
        {Localizer.localize('gaming.actions.play')}
      </ButtonFX>
    );
  };

  renderTVMainButton = (): React.Node => {
    const {
      broadcastStatus,
      defaultOnItemClick,
      item,
      item: { locType },
      onItemClick,
      previewCatchupScheduledEventId,
      programMetadata,
      seriesMetadata,
      tileConfig: { type: tileType },
      tvLocationMetadata,
    } = this.props;

    let mainButton = null;

    let cardDataUrlDefinition: Undefined<NETGEM_API_V8_URL_DEFINITION> = undefined;
    if (tvLocationMetadata) {
      // In case of TV item, add the program/series card URL definition (if found) so that the episodes and the related items will be displayed on the card
      const urlDefinition = getCardUrlDefinitionForItem(item, seriesMetadata, onItemClick, defaultOnItemClick);
      if (urlDefinition) {
        cardDataUrlDefinition = urlDefinition;
      }
    }

    if (broadcastStatus === BroadcastStatus.Future) {
      // Future scheduled event
      mainButton = (
        <RecordButton
          broadcastStatus={broadcastStatus}
          cardDataUrlDefinition={cardDataUrlDefinition}
          context={RecordContext.Tile}
          displayMode={DisplayMode.Button}
          item={item}
          key='recordButton'
          location={tvLocationMetadata?.location ?? null}
          previewCatchupScheduledEventId={previewCatchupScheduledEventId}
          programMetadata={programMetadata}
          seriesMetadata={seriesMetadata}
          tileType={tileType}
        />
      );
    } else if (broadcastStatus !== BroadcastStatus.Unknown) {
      // Live scheduled event, catchup or recording
      const progress = broadcastStatus === BroadcastStatus.Live ? null : this.localGetWatchingStatus();
      const buttonTextKey = broadcastStatus === BroadcastStatus.Live && locType !== ItemLocationKind.Recording ? 'common.actions.watch_live' : this.getWatchButtonLabel(progress);
      const widthKind = tileType === TileConfigTileType.LandscapeBig || tileType === TileConfigTileType.LandscapeVod ? WidthKind.Large : WidthKind.Stretched;
      mainButton = (
        <ButtonFX
          allowZeroProgress={broadcastStatus === BroadcastStatus.Live}
          heightKind={HeightKind.Medium}
          key='play'
          onClick={this.handlePlayButtonOnClick}
          progress={progress}
          widthKind={widthKind}
        >
          {Localizer.localize(buttonTextKey)}
        </ButtonFX>
      );
    } else if (seriesMetadata !== null) {
      mainButton = this.renderSeriesPlayButton();
    }

    return mainButton;
  };

  handleSynopsisOnResize = (data: ResizeType): void => {
    const {
      item: { locType },
    } = this.props;
    const { isLoading, synopsis } = this.state;
    const { height: availableHeight } = data;

    if (isLoading || !synopsis || availableHeight === null || availableHeight === 0) {
      // Component not mounted
      return;
    }

    // Synopsis wrapper has been rendered

    const isTagLine = locType === ItemLocationKind.TVOD || locType === ItemLocationKind.EST || locType === ItemLocationKind.Game;
    const lineHeight = isTagLine ? TILE_SYNOPSIS_TAGLINE_LINE_HEIGHT : TILE_SYNOPSIS_LINE_HEIGHT;

    // Determine how many lines fit within the container height
    const maxLines = Math.floor(availableHeight / lineHeight);

    this.setState((prevState: ItemOverlayStateType) => {
      if (prevState.maxLines === maxLines && prevState.truncateState !== TruncateState.Initializing) {
        return null;
      }

      return {
        maxLines,
        truncateState: TruncateState.NotTruncated,
      };
    });
  };

  checkTruncation = (node: ?HTMLElement) => {
    const { truncateState } = this.state;

    if (!node) {
      return;
    }

    // ScrollHeight > clientHeight indicates the text overflows the available vertical space
    const isOverflowing = node.scrollHeight > node.clientHeight;
    const newTruncateState = isOverflowing ? TruncateState.Truncated : TruncateState.NotTruncated;

    if (truncateState !== newTruncateState) {
      this.setState({ truncateState: newTruncateState });
    }
  };

  renderSynopsis = (): React.Node => {
    const {
      item: { locType },
    } = this.props;
    const { maxLines, synopsis } = this.state;

    const isTagLine = locType === ItemLocationKind.TVOD || locType === ItemLocationKind.EST || locType === ItemLocationKind.Game;

    return (
      <div className={clsx('synopsisWrapper', (synopsis === null || synopsis === '') && 'empty', isTagLine && 'tagLine')}>
        <div
          className='synopsisText'
          ref={this.checkTruncation}
          style={{
            WebkitLineClamp: maxLines > 0 ? maxLines : undefined,
          }}
        >
          <span className='screenReaderOnly'>{Localizer.localize('accessibility.content.synopsis')}</span>
          {synopsis}
        </div>
      </div>
    );
  };

  renderPricingVod = (): React.Node => {
    const {
      item: { purchasable, type },
      purchaseInfo,
      tileConfig: { type: tileType },
      vodLocationsMetadata,
      vodStatus,
    } = this.props;

    if (!vodStatus && !purchaseInfo) {
      return null;
    }

    let status = BOVodAssetStatus.Unknown;
    const expirationTime = vodStatus?.expirationTime ?? 0;

    if (vodStatus) {
      ({ status } = vodStatus);
    } else if (purchaseInfo) {
      status = BOVodAssetStatus.Locked;
    }

    if (status === BOVodAssetStatus.Unknown) {
      // Purchase list not loaded yet
      return null;
    }

    if ((purchasable !== undefined && !purchasable) || status === BOVodAssetStatus.Free) {
      // No message on tiles for free VOD items
      return null;
    }

    // TODO: handle BOVodAssetStatus.FreeButAvailableInFuture here if some movies with future availability appear in the catalog

    const context = tileType === TileConfigTileType.LandscapeBig || tileType === TileConfigTileType.LandscapeVod ? PricingContext.LandscapeTile : PricingContext.RegularTile;

    return (
      <PricingVod
        context={context}
        expirationTime={expirationTime}
        isVodSeries={type === ItemType.Series}
        locationsMetadata={vodLocationsMetadata}
        onClick={this.handlePurchaseButtonOnClick}
        purchaseInfo={purchaseInfo}
        status={status}
      />
    );
  };

  renderHoverContentElements = (): React.Node => {
    const {
      hoverContent,
      item: { locType },
      programMetadataStatus,
      programTitle,
      seriesMetadataStatus,
      seriesTitle,
    } = this.props;

    let titleElement = null;
    let subtitleElement = null;
    let synopsisElement = null;

    // Series title
    if (hoverContent.has(TileConfig.SeriesTitle)) {
      titleElement = renderSeriesTitle(programMetadataStatus, seriesMetadataStatus, seriesTitle);
    }

    // Program title
    if (hoverContent.has(TileConfig.ProgramTitle)) {
      const programTitleElement = renderProgramTitle(programMetadataStatus, seriesMetadataStatus, programTitle, null, titleElement ? 'secondLineInfo' : '', titleElement === null);
      if (titleElement) {
        subtitleElement = programTitleElement;
      } else {
        titleElement = programTitleElement;
      }
    }

    // Date & duration
    const timeElement = this.getTimeElement(hoverContent, 'thirdLineInfo');

    // Year, genre, language & parental guidance
    const detailsElement = this.getDetailsElement(hoverContent);

    if (hoverContent.has(TileConfig.ProgramSynopsis) || hoverContent.has(TileConfig.SeriesSynopsis) || locType === ItemLocationKind.Game) {
      synopsisElement = (
        <ResizeDetectorWrapper className='resizeDetectorSynopsis' handleHeight handleWidth onResize={this.handleSynopsisOnResize} refreshMode='debounce' refreshRate={50}>
          {this.renderSynopsis()}
        </ResizeDetectorWrapper>
      );
    }

    return (
      <>
        <TextScroller scrollOnMount style={{ font: '18px var(--bold-font)', maxHeight: '24px', transform: 'scale3d(1.22, 1.22, 1)' }}>
          {titleElement}
        </TextScroller>
        {subtitleElement ? (
          <TextScroller scrollOnMount style={{ font: '11px var(--light-font)', maxHeight: '16px' }}>
            {subtitleElement}
          </TextScroller>
        ) : null}
        {timeElement}
        {detailsElement}
        {synopsisElement}
      </>
    );
  };

  renderUnknownProgram = (): React.Node => {
    const {
      isInLiveSection,
      onClick,
      tileConfig: { type: tileType },
    } = this.props;

    if (!isInLiveSection) {
      // Do not display overlay when nothing is known about program
      return null;
    }

    const widthKind = tileType === TileConfigTileType.LandscapeBig || tileType === TileConfigTileType.LandscapeVod ? WidthKind.Large : WidthKind.Stretched;

    // No info for current program but since it's a live tile, allow to open player with channel only
    return (
      <div className='overlay bottom' onClick={onClick}>
        <ButtonFX heightKind={HeightKind.Medium} key='play' onClick={this.handlePlayButtonOnClick} widthKind={widthKind}>
          {Localizer.localize('common.actions.watch_live')}
        </ButtonFX>
      </div>
    );
  };

  renderMainButton = (): React.Node => {
    const { contentType } = this.props;

    switch (contentType) {
      case ItemContent.VODOrDeeplink:
        return this.renderVodMainButton();

      case ItemContent.TV:
      case ItemContent.NetgemSVOD:
        return this.renderTVMainButton();

      case ItemContent.Game:
        return this.renderGameMainButton();

      default:
        return null;
    }
  };

  render(): React.Node {
    const {
      isVisible,
      item: {
        selectedLocation: { id },
      },
      onClick,
      programMetadata,
      tileConfig: { onFocus },
    } = this.props;
    const { isLoading } = this.state;

    if (!isVisible || onFocus === TileOnFocus.Selection) {
      // Tile is not hovered: overlay is not displayed
      return null;
    }

    if (!programMetadata) {
      return this.renderUnknownProgram();
    }

    const mainButton = this.renderMainButton();

    // Information displayed over tile, described by hoverContent in tileConfig (series tiles, program title, etc.)
    const hoverContentElements = this.renderHoverContentElements();

    return (
      <div className='overlay' id={`tile-overlay-${id}`} onClick={onClick}>
        {isLoading ? <InfiniteCircleLoader className='loader' /> : hoverContentElements}
        {mainButton}
      </div>
    );
  }
}

const mapStateToProps = (state: CombinedReducers): ReduxItemOverlayReducerStateType => {
  return {
    appConfiguration: state.appConfiguration,
    channels: state.appConfiguration.deviceChannels,
    commercialOffers: state.appConfiguration.rightsCommercialOffers,
    defaultOnItemClick: state.ui.defaultActions ? state.ui.defaultActions.onItemClick : null,
    defaultRights: state.appConfiguration.rightsDefault,
    isRegisteredAsGuest: state.appRegistration.registration === RegistrationType.RegisteredAsGuest,
    isSubscriptionFeatureEnabled: state.appConfiguration.features[FEATURE_SUBSCRIPTION],
    playingHistory: PlayingHistoryCache.checkGet(state.ui.playingHistory, state.ui.playingHistoryStatus, true),
    playingHistoryStatus: state.ui.playingHistoryStatus,
    purchaseList: state.appRegistration.registration === RegistrationType.RegisteredAsGuest ? {} : state.ui.purchaseList,
    userRights: state.appConfiguration.rightsUser,
    viewingHistory: state.ui.viewingHistory,
    viewingHistoryStatus: state.ui.viewingHistoryStatus,
  };
};

const mapDispatchToProps = (dispatch: Dispatch): ReduxItemOverlayDispatchToPropsType => {
  return {
    localGetGamestreamToken: (item: NETGEM_API_V8_FEED_ITEM, signal?: AbortSignal): Promise<GamestreamToken> => dispatch(getGamestreamToken(item, signal)),

    localHideModal: (): void => dispatch(hideModal()),

    localShowConfirmation: (data: CONFIRMATION_DATA_MODAL_TYPE, ariaLabel: string): void => dispatch(showConfirmationModal(data, ariaLabel)),

    localShowEula: (data: EULA_DATA_MODAL_TYPE): void => dispatch(showEulaModal(data)),

    localShowGameProfileSelect: (data: GAME_PROFILE_SELECT_DATA_MODAL_TYPE): void => dispatch(showGameProfileSelectModal(data)),

    localShowMessage: (messageData: MESSAGE_MODAL_TYPE, ariaLabel: string): void => dispatch(showMessageModal(messageData, ariaLabel)),

    localUpdatePlayingHistory: (playingHistoryItem: NETGEM_API_PLAYINGHISTORY_ITEM, itemId: string): Promise<void> => dispatch(updatePlayingHistory(playingHistoryItem, itemId)),

    showVodPurchaseDialog: (vodPurchaseData: VOD_PURCHASE_DATA_TYPE): void => dispatch(showVodPurchaseModal(vodPurchaseData)),
  };
};

const ItemOverlay: React.ComponentType<ItemOverlayPropType> = connect(mapStateToProps, mapDispatchToProps)(ItemOverlayView);

export { ItemOverlayView };
export default ItemOverlay;
